<template>
    <div class="detail-container">
        <h3 class="text-center mt-1">Phase Determination Form</h3>
        <hr />

        <div class="mt-4 text-end mb-3" v-if="data.length > 0">
            <button class="btn btn-sm btn-warning"
                @click="$router.push({ name: 'PhaseDeterminationEdit', params: { id: data[0].id } })">
                ✏️ Request/Change Phase and Schedule
            </button>
            <button class="btn btn-sm btn-warning ms-2" @click="$router.push({
                name: 'PhaseDeterminationEdit',
                params: { id: data[0].id },
                query: { updateOnly: 'yes' }
            })">
                ✏️ Make Corrections
            </button>

        </div>

        <div v-if="loading" class="loading-overlay">
            <div class="spinner"></div>
        </div>

        <div v-else-if="errorMessage" class="error-message">{{ errorMessage }}</div>

        <div v-else>

            <div v-for="(item, index) in data" :key="item.id" :ref="'pdf-' + item.id"
                class="form-section border rounded-3 p-3 mb-3 bg-white">
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <strong class="text-danger">Last Modified: {{ $formatDateTime(item.updated_at) }} </strong>
                    <button v-if="index !== 0" class="btn btn-sm btn-outline-secondary" @click="toggle(item.id)">
                        {{ expandedId === item.id ? '➖ Collapse' : '➕ Expand' }}
                    </button>
                </div>

                <div v-show="expandedId === item.id || index === 0">
                    <div class="row">
                        <div><strong>Patient Name:</strong> {{ item.student?.user?.name || '-' }}</div>
                        <div><strong>Admit Date:</strong> {{ $formatDate(item.admit_date) }}</div>
                    </div>

                    <div class="row">
                        <div><strong>Current Dose:</strong> {{ item.dose }}</div>
                        <div><strong>Time in Treatment:</strong> {{ item.time_in_treatment_days }} days</div>
                    </div>

                    <!-- <div class="row">
                        <div><strong>Two non-reactive drug screens in past 60 days:</strong>
                            {{ item.has_two_nonreactive_screens ? 'Yes' : 'No' }}
                        </div>
                    </div> -->

                    <div class="row">
                        <div><strong>Current Phase:</strong> {{ item.current_phase }}</div>
                        <div><strong>Current Schedule:</strong>
                            <span v-for="day in ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']"
                                :key="day" style="margin-right: 10px;">
                                <span
                                    v-if="Array.isArray(item.current_schedule) && item.current_schedule.includes(day)">✔️</span>
                                <span v-else>⭕</span> {{ day }}
                            </span>
                        </div>
                    </div>

                    <div class="row">
                        <div><strong>Requested Phase:</strong> {{ item.requested_phase }}</div>
                        <div><strong>Requested Schedule:</strong>
                            <span v-for="day in ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']"
                                :key="day" style="margin-right: 10px;">
                                <span
                                    v-if="Array.isArray(item.requested_schedule) && item.requested_schedule.includes(day)">✔️</span>
                                <span v-else>⭕</span> {{ day }}
                            </span>
                        </div>
                    </div>
                    <div>
                        <h4 class="mt-4">CFR 42 8.12 criteria for take-home (unsupervised) doses:</h4>

                        <div class="mb-3">
                            <strong>1)</strong> Absence of active substance use disorders...:
                            <span class="ms-2 fw-bold">{{ item.take_home_criteria?.no_active_substance_disorder === 1 ?
                                'Yes' :
                                'No' }}</span>
                        </div>

                        <div class="mb-3">
                            <strong>2)</strong> Regularity of attendance for supervised medication administration:
                            <span class="ms-2 fw-bold">{{ item.take_home_criteria?.regular_attendance === 1 ? 'Yes' :
                                'No'
                                }}</span>
                        </div>

                        <div class="mb-3">
                            <strong>3)</strong> Absence of serious behavioral problems that endanger the patient, public
                            or others:
                            <span class="ms-2 fw-bold">{{ item.take_home_criteria?.no_behavioral_issues === 1 ? 'Yes' :
                                'No'
                            }}</span>
                        </div>

                        <div class="mb-3">
                            <strong>4)</strong> Absence of known recent diversion activity:
                            <span class="ms-2 fw-bold">{{ item.take_home_criteria?.no_diversion_activity === 1 ? 'Yes' :
                                'No'
                                }}</span>
                        </div>

                        <div class="mb-3">
                            <strong>5)</strong> Whether take-home medication can be safely transported and stored:
                            <span class="ms-2 fw-bold">{{ item.take_home_criteria?.can_safely_store_medication === 1 ?
                                'Yes' :
                                'No' }}</span>
                        </div>

                        <div class="mb-3">
                            <strong>6)</strong> Any other criteria :
                            <div class="note-box">{{ item.take_home_criteria?.other_considerations || '-' }}
                            </div>
                        </div>
                    </div>


                    <h4 class="mt-3">Counselor Impressions:</h4>
                    <div v-if="editImpressionId === item.id">
                        <textarea v-model="editImpression" rows="4" class="form-control mb-2"></textarea>
                        <button class="btn btn-sm btn-success me-2" @click="saveImpression(item.id)">💾 Save</button>
                        <button class="btn btn-sm btn-secondary" @click="cancelEdit">✖️ Cancel</button>
                    </div>
                    <div v-else class="note-box">{{ item.counselor_impression || '' }}</div>

                    <button v-if="isCounselor && editImpressionId !== item.id" class="btn btn-primary mb-3"
                        id="counselor_impression_update" @click="startEditImpression(item)">
                        Update Counselor Impressions
                    </button>

                    <h4 class="mt-3">Practitioner Review:</h4>
                    <div class="note-box">{{ item.physician_review?.review_note || '' }}</div>

                    <h4 class="mt-3">Practitioner Determination:</h4>
                    <div class="note-box">{{ item.physician_determination?.determination_note || '' }}</div>
                    <div><strong>PMP Report Reviewed:</strong>
                        {{ item.physician_determination?.pmp_report_reviewed === true ? 'Yes' :
                            item.physician_determination?.pmp_report_reviewed === false ? 'No' : '' }}
                    </div>
                    <div><strong>Result Notes:</strong> {{ item.physician_determination?.result_notes || '' }}</div>

                    <h4 class="mt-3">Approved Schedule:</h4>
                    <div class="note-box">
                        {{ phaseLabel(item.physician_determination?.approved_phase) }} —
                        {{ getScheduleList(item.physician_determination?.approved_schedule) }}

                    </div>

                    <div class="sign-section mt-4">
                        <div><strong>Counselor Signature:</strong> <span
                                :class="{ 'signature-font': item.counselor_id !== 0 && item.counselor_id !== null }"><u>
                                    {{ item.counselor?.user?.name || '________________' }}</u>
                            </span>
                            &nbsp;&nbsp; &nbsp;Date: {{ item.impression_date ? $formatDate(item.impression_date) : '' }}

                        </div>
                        <div><strong>Practitioner Signature:</strong> <span
                                :class="{ 'signature-font': item.practitioner_id !== 0 && item.practitioner_id !== null }"><u>
                                    {{ item.practitioner?.user?.name || '________________' }}</u>
                            </span> &nbsp; &nbsp; &nbsp;Date:
                            {{ $formatDate(item.practitioner_date) || '-' }}
                        </div>

                        <button v-if="$canAccess('PractitionerSignaturePhaseDetermination')"
                            class="btn btn-sm btn-outline-primary" @click="$router.push({
                                name: 'PractitionerSignaturePhaseDetermination',
                                params: { id: item.id },
                                query: { action: 'update' }
                            })">
                            🖊️ Update Reviews and Approve Phase
                        </button>



                    </div>
                </div>
                <div class="text-end mt-3">
                    <button class="btn btn-sm btn-outline-success" @click="downloadPDF(item.id)">
                        📥 Download PDF
                    </button>
                </div>
            </div>
        </div>

        <div class="text-center mt-3" v-if="data.length == 0 && $canAccess('PhaseDeterminationAdd')">
            <button class="btn btn-sm btn-primary"
                @click="$router.push({ name: 'PhaseDeterminationAdd', query: { student_id: student_id } })">
                ➕ Add New Phase Determination
            </button>
        </div>
    </div>
</template>

<script>

export default {
    props: ['student_id'],
    data() {
        return {
            data: [],
            loading: false,
            errorMessage: null,
            expandedId: null,
            editImpressionId: null,
            editImpression: '',
            default_counselor_expression: '',
            current_date: ''
        };
    },
    computed: {
        isCounselor() {
            return this.$store.getters.getUser?.role === 'counselor';
        }
    },
    methods: {
        async downloadPDF(id) {
            try {
                const response = await this.$api.get(`/phase-determinations/${id}/export-pdf`, {
                    responseType: 'blob'  // 🔥 this is important!
                });

                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `phase-determination-${id}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error('Download failed', error);
                this.$toast?.error('Failed to download PDF');
            }
        }
        ,

        getScheduleList(value) {
            try {
                const parsed = typeof value === 'string' ? JSON.parse(value) : value;
                return Array.isArray(parsed) ? parsed.join(', ') : '-';
            } catch {
                return '-';
            }
        },
        phaseLabel(phase) {
            const map = {
                0: 'Phase 0',
                1: 'Phase 1',
                2: 'Phase 2',
                3: 'Phase 3',
                4: 'Phase 4',
            };
            return map[phase] || '-';
        },
        setDefaultCounselorExpression(phase) {
            this.default_counselor_expression = "The patient is currently on phase " + phase.current_phase + " and has remained compliant with program expectations and take-home medication guidelines. They attend dosing and counselling appointments consistently, are on a stable methadone dose, and have demonstrated the ability to safely store medication in a secure lockbox. The 8-point federal criteria have been reviewed, and it is the clinical judgment that the benefits of a phase increase outweigh the risks. Urine drug screens have been negative for illicit substances and alcohol. The patient is appropriate for a phase increase to 6 take-home doses (THDs).";
        },
        toggle(id) {
            if (this.data.length && this.data[0].id === id) return;
            this.expandedId = this.expandedId === id ? null : id;
        },
        startEditImpression(item) {
            this.editImpressionId = item.id;

            this.setDefaultCounselorExpression(item);
            this.editImpression = item.counselor_impression || this.default_counselor_expression;
        },
        cancelEdit() {
            this.editImpressionId = null;
            this.editImpression = '';
        },
        async saveImpression(id) {
            try {
                await this.$api.put(`/phase-determinations/${id}/update-counselor-impression`, {
                    counselor_impression: this.editImpression,
                });
                const updated = this.data.find(item => item.id === id);
                if (updated) updated.counselor_impression = this.editImpression;
                this.cancelEdit();
                this.load();
            } catch (error) {
                alert('Failed to update impression.');
            }
        },
        async load() {
            this.loading = true;
            this.expandedId = null;
            this.data = [];
            try {
                const res = await this.$api.get(`/phase-determinations/by-student/${this.student_id}`);
                this.data = res.data || [];
                this.expandedId = this.data.length > 0 ? this.data[0].id : null;

            } catch (err) {
                this.errorMessage = 'Unable to load data';
            } finally {
                this.loading = false;
            }
        }
    },
    async mounted() {

        this.current_date = new Date().toISOString().slice(0, 10); // Format: YYYY-MM-DD

        await this.load();


    }
}
</script>


<style scoped>
.detail-container {
    padding: 20px;
    background: white;
    border-radius: 10px;
    margin: auto;
}

.row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
}

.note-box {
    border: 1px solid #ccc;
    padding: 8px;
    min-height: 50px;
    border-radius: 5px;
    background-color: #f9f9f9;
    margin-bottom: 10px;
}

.sign-section div {
    margin-top: 10px;
}

.loading-overlay {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #ccc;
    border-top-color: #000;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.error-message {
    color: red;
    text-align: center;
    margin-top: 20px;
}

@media print {
    .no-print {
        display: none !important;
    }
}
</style>
